'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  dropFromUnsavedLists,
  readStored,
  readUnsavedList,
  writeAttachmentList,
} from './unsaved-attachment-lists'

type Scope = {
  orgId: string
  brandId: string | undefined
  chatId: string | null
}

function isSameScope(a: Scope, b: Scope) {
  return a.orgId === b.orgId && a.brandId === b.brandId && a.chatId === b.chatId
}

/**
 * An image the user staged onto the chat composer from the brand assets
 * gallery. At submit time each entry becomes a standard AI SDK `file`
 * part (`{ type: 'file', url, mediaType, filename }`) so the orchestrator
 * route's existing image pipeline (clamp → rehost → `userImageUrls` →
 * email-agent `imageUrls` forwarding) applies with no server changes.
 */
export type AttachedComposerImage = {
  /** Canonical hosted CDN URL — the identity key for the lattice. */
  url: string
  /** Resolved at attach time (extension sniff / logo format). */
  mediaType: string
  /** Display slug shown on the chip and in the transcript attachment. */
  filename?: string
  source: 'brand-asset'
}

/**
 * Staging cap. Keeps a click-happy selection session from ballooning the
 * user message with dozens of image parts (each becomes a model-visible
 * image block). `attachImage` reports a rejected attach so the calling
 * surface can explain the limit.
 */
export const MAX_ATTACHED_COMPOSER_IMAGES = 8

export const ATTACHED_IMAGES_PREFIX = 'canvas-chat-attached-images-v1'

/**
 * Build the per-(orgId, brandId, chatId) localStorage key that holds the
 * "attached to chat composer" images. Scoped per chat tab (parallel chats
 * stage independently); `brandId` falls back to `'none'` to mirror
 * `getAttachedEmailsKey` in `use-chat-attached-emails.ts`.
 */
export function getAttachedImagesKey(
  orgId: string,
  brandId: string | undefined,
  chatId: string
): string {
  const brandSuffix = brandId ?? 'none'
  return `${ATTACHED_IMAGES_PREFIX}:${orgId}:${brandSuffix}:${chatId}`
}

function isValidStored(value: unknown): value is AttachedComposerImage {
  if (!value || typeof value !== 'object') {
    return false
  }
  const candidate = value as {
    url?: unknown
    mediaType?: unknown
    filename?: unknown
    source?: unknown
  }
  if (typeof candidate.url !== 'string' || candidate.url.length === 0) {
    return false
  }
  if (
    typeof candidate.mediaType !== 'string' ||
    candidate.mediaType.length === 0
  ) {
    return false
  }
  if (
    candidate.filename !== undefined &&
    typeof candidate.filename !== 'string'
  ) {
    return false
  }
  return candidate.source === 'brand-asset'
}

function normalizeForStorage(
  image: AttachedComposerImage
): AttachedComposerImage {
  return {
    url: image.url,
    mediaType: image.mediaType,
    ...(image.filename ? { filename: image.filename } : {}),
    source: 'brand-asset',
  }
}

/** A valid image's identity, to match another tab's changes by. */
function urlOf(entry: unknown): string | undefined {
  return isValidStored(entry) ? entry.url : undefined
}

export function readStoredAttachedImages(
  orgId: string,
  brandId: string | undefined,
  chatId: string
): Array<AttachedComposerImage> {
  if (typeof window === 'undefined') {
    return []
  }
  const key = getAttachedImagesKey(orgId, brandId, chatId)
  const unsaved = readUnsavedList<AttachedComposerImage>(key)
  if (unsaved) {
    return unsaved.slice(0, MAX_ATTACHED_COMPOSER_IMAGES)
  }
  try {
    const raw = readStored(key)
    if (!raw) {
      return []
    }
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter(isValidStored).slice(0, MAX_ATTACHED_COMPOSER_IMAGES)
  } catch {
    return []
  }
}

export function writeStoredAttachedImages(
  orgId: string,
  brandId: string | undefined,
  chatId: string,
  images: ReadonlyArray<AttachedComposerImage>
): void {
  if (typeof window === 'undefined') {
    return
  }
  // Storage refusing the write (quota / disabled storage) keeps the list in
  // memory for this page, which the read above prefers.
  writeAttachmentList(
    getAttachedImagesKey(orgId, brandId, chatId),
    images.map(normalizeForStorage),
    urlOf
  )
}

/**
 * Remove `url` from EVERY stored lattice for this (orgId, brandId) — all
 * chat tabs, not just the active one. Used when the underlying brand
 * asset is deleted: the active tab detaches through React state, but
 * other tabs' staged attachments live only in localStorage and would
 * otherwise resurrect the deleted URL on their next rehydration.
 */
export function purgeUrlFromStoredAttachedImages(
  orgId: string,
  brandId: string | undefined,
  url: string
): void {
  if (typeof window === 'undefined') {
    return
  }
  const prefix = `${ATTACHED_IMAGES_PREFIX}:${orgId}:${brandId ?? 'none'}:`
  // Lists storage refused are read before storage, so they drop it too,
  // in this tab and the app's other tabs.
  dropFromUnsavedLists(prefix, url)
  try {
    // Snapshot matching keys via the standard length/key API first —
    // we mutate storage while iterating, and `Object.keys(localStorage)`
    // is not portable across storage shims.
    const keys: Array<string> = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(prefix)) {
        keys.push(key)
      }
    }
    for (const key of keys) {
      const raw = localStorage.getItem(key)
      if (!raw) {
        continue
      }
      let parsed: unknown
      try {
        parsed = JSON.parse(raw)
      } catch {
        continue
      }
      if (!Array.isArray(parsed)) {
        continue
      }
      const kept = parsed.filter(
        (entry) => !isValidStored(entry) || entry.url !== url
      )
      if (kept.length === parsed.length) {
        continue
      }
      if (kept.length === 0) {
        localStorage.removeItem(key)
      } else {
        localStorage.setItem(key, JSON.stringify(kept))
      }
    }
  } catch {
    // ignore storage errors
  }
}

/**
 * Merge `incoming` onto the end of `target` — "New chat" carrying the
 * composer's staged images. Duplicate URLs keep their first entry, and the
 * result respects {@link MAX_ATTACHED_COMPOSER_IMAGES}.
 */
export function mergeAttachedImages(
  target: ReadonlyArray<AttachedComposerImage>,
  incoming: ReadonlyArray<AttachedComposerImage>
): Array<AttachedComposerImage> {
  const seen = new Set(target.map((image) => image.url))
  const merged = [...target]
  for (const image of incoming) {
    if (seen.has(image.url)) {
      continue
    }
    seen.add(image.url)
    merged.push(normalizeForStorage(image))
  }
  return merged.slice(0, MAX_ATTACHED_COMPOSER_IMAGES)
}

export type AttachImageResult =
  | 'attached'
  | 'duplicate'
  | 'over-cap'
  | 'no-chat'

export type UseChatAttachedImagesReturn = {
  attachedImages: Array<AttachedComposerImage>
  /** URL-keyed view of the lattice — cards derive "selected" from this. */
  attachedImageUrls: ReadonlySet<string>
  /**
   * Append `image` to the lattice. Duplicate URLs and attaches past
   * {@link MAX_ATTACHED_COMPOSER_IMAGES} are rejected (the result says
   * why so the calling surface can toast the cap).
   */
  attachImage: (image: AttachedComposerImage) => AttachImageResult
  detachImage: (url: string) => void
  clearAttachedImages: () => void
  /**
   * Remove `url` from the LIVE tab's lattice AND every stored lattice of
   * the (org, brand) scope. For asset deletion: unlike `detachImage`
   * (scope-guarded — a stale closure must not touch another tab), purging
   * a deleted asset's URL is correct on WHATEVER tab is live when the
   * delete round-trip resolves, so this callback reads the live scope
   * from refs instead of its closure.
   */
  purgeImageUrl: (url: string) => void
  /**
   * Move the live chat's staged images onto `targetChatId` — "New chat"
   * calls it with the chat it just opened.
   */
  moveStagedTo: (targetChatId: string) => void
}

/**
 * Manage the per-chat-tab "attached to chat composer" images set — the
 * image twin of `useChatAttachedEmails`. Source of truth is in-memory
 * state, mirrored to a per-`(orgId, brandId, chatId)` localStorage key so
 * staged attachments survive reloads. Cleared explicitly on submit (the
 * chat thread calls `clearAttachedImages()`), by card deselection, or by
 * the chip's remove button.
 *
 * `chatId === null` is treated as "no active chat"; the hook returns an
 * empty list and no-ops on writes.
 */
export function useChatAttachedImages(args: {
  orgId: string
  brandId?: string
  chatId: string | null
}): UseChatAttachedImagesReturn {
  const { orgId, brandId, chatId } = args

  // Initialize from storage synchronously so chips render on first paint
  // after a reload. Re-hydration on scope change uses the React 19
  // "derive state from changing prop" pattern (compare scope on render,
  // call `setState` directly during render) — same rationale as
  // `useChatAttachedEmails`.
  const [attachedImages, setAttachedImages] = useState<
    Array<AttachedComposerImage>
  >(() => (chatId ? readStoredAttachedImages(orgId, brandId, chatId) : []))
  const [trackedScope, setTrackedScope] = useState<Scope>({
    orgId,
    brandId,
    chatId,
  })
  // The read prefers a list storage refused (see
  // `unsaved-attachment-lists.ts`), same as `useChatAttachedEmails`.
  if (!isSameScope(trackedScope, { orgId, brandId, chatId })) {
    setTrackedScope({ orgId, brandId, chatId })
    setAttachedImages(
      chatId ? readStoredAttachedImages(orgId, brandId, chatId) : []
    )
  }

  const persist = useCallback(
    (next: Array<AttachedComposerImage>) => {
      setAttachedImages(next)
      if (chatId) {
        writeStoredAttachedImages(orgId, brandId, chatId, next)
      }
    },
    [orgId, brandId, chatId]
  )

  const attachedImagesRef = useRef(attachedImages)
  useEffect(() => {
    attachedImagesRef.current = attachedImages
  }, [attachedImages])

  // Guards the mutators against STALE closures held across await gaps
  // (e.g. a delete flow captured before a tab switch): a stale callback's
  // captured `chatId` no longer matches the live one, and letting it run
  // would write the NEW tab's list (read from the shared ref) under the
  // OLD tab's storage key.
  const liveChatIdRef = useRef(chatId)
  const liveScopeRef = useRef<Scope>({ orgId, brandId, chatId })
  useEffect(() => {
    liveChatIdRef.current = chatId
    liveScopeRef.current = { orgId, brandId, chatId }
  }, [orgId, brandId, chatId])

  const attachImage = useCallback(
    (image: AttachedComposerImage): AttachImageResult => {
      if (!chatId) {
        return 'no-chat'
      }
      if (liveChatIdRef.current !== chatId) {
        return 'no-chat'
      }
      const current = attachedImagesRef.current
      if (current.some((entry) => entry.url === image.url)) {
        return 'duplicate'
      }
      if (current.length >= MAX_ATTACHED_COMPOSER_IMAGES) {
        return 'over-cap'
      }
      persist([...current, normalizeForStorage(image)])
      return 'attached'
    },
    [chatId, persist]
  )

  const detachImage = useCallback(
    (url: string) => {
      if (!chatId) {
        return
      }
      if (liveChatIdRef.current !== chatId) {
        return
      }
      const current = attachedImagesRef.current
      if (!current.some((entry) => entry.url === url)) {
        return
      }
      persist(current.filter((entry) => entry.url !== url))
    },
    [chatId, persist]
  )

  const clearAttachedImages = useCallback(() => {
    if (!chatId) {
      return
    }
    if (liveChatIdRef.current !== chatId) {
      return
    }
    if (attachedImagesRef.current.length === 0) {
      return
    }
    persist([])
  }, [chatId, persist])

  const moveStagedTo = useCallback(
    (targetChatId: string) => {
      if (!chatId || targetChatId === chatId) {
        return
      }
      if (liveChatIdRef.current !== chatId) {
        return
      }
      const current = attachedImagesRef.current
      if (current.length === 0) {
        return
      }
      const moved = mergeAttachedImages(
        readStoredAttachedImages(orgId, brandId, targetChatId),
        current
      )
      writeStoredAttachedImages(orgId, brandId, targetChatId, moved)
      persist([])
    },
    [chatId, orgId, brandId, persist]
  )

  // Deliberately NOT scope-guarded (see the return-type docs): the delete
  // round-trip may resolve after a tab switch, and the deleted URL must
  // leave whichever tab is live NOW (its state rehydrated from storage
  // that still held the URL) or the next persist resurrects it.
  const purgeImageUrl = useCallback((url: string) => {
    const scope = liveScopeRef.current
    const current = attachedImagesRef.current
    if (current.some((entry) => entry.url === url)) {
      const next = current.filter((entry) => entry.url !== url)
      setAttachedImages(next)
      attachedImagesRef.current = next
      if (scope.chatId) {
        writeStoredAttachedImages(
          scope.orgId,
          scope.brandId,
          scope.chatId,
          next
        )
      }
    }
    purgeUrlFromStoredAttachedImages(scope.orgId, scope.brandId, url)
  }, [])

  const attachedImageUrls = useMemo(
    () =>
      new Set(attachedImages.map((image) => image.url)) as ReadonlySet<string>,
    [attachedImages]
  )

  // Memoize the return shape — this feeds the GlobalChatContext value
  // memo, so a fresh object identity per render would re-render every
  // chat consumer (same rationale as `useChatAttachedEmails`).
  return useMemo(
    () => ({
      attachedImages,
      attachedImageUrls,
      attachImage,
      detachImage,
      clearAttachedImages,
      purgeImageUrl,
      moveStagedTo,
    }),
    [
      attachedImages,
      attachedImageUrls,
      attachImage,
      detachImage,
      clearAttachedImages,
      purgeImageUrl,
      moveStagedTo,
    ]
  )
}
